/**
 * 4.1 — CONSENTED DEVICE LOCATION, FOREGROUND ONLY.
 *
 * Founder authorization (2026-06): for "here", "near me" and local weather, Arvexis should use
 * the device's current location once the member grants normal browser permission, instead of
 * asking them for a city every time.
 *
 * Rules this file exists to keep:
 *   - Nothing is requested until the backend says this turn genuinely needs it. There is no
 *     permission prompt on load, no prompt on an ordinary turn, and no watchPosition.
 *   - One fix, for one request. It is handed to that request and then dropped: it is never
 *     cached across turns, never written to storage, and never sent with a turn that does not
 *     need it.
 *   - Only the precision the task needs: low accuracy is requested, and the backend coarsens to
 *     ~1 km before anything leaves the process.
 *   - A denial is a normal answer, not an error. She asks for a city instead.
 */

const FIX_TIMEOUT_MS = 8000;

/**
 * Ask the device where it is, once, for this request.
 * Returns { permission: 'granted'|'denied'|'unsupported', location|null }.
 */
export async function requestDeviceLocation() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { permission: 'unsupported', location: null };
  }
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,   // city-level is all a forecast needs
        timeout: FIX_TIMEOUT_MS,
        maximumAge: 5 * 60 * 1000,   // a fix from the last few minutes is still "now"
      });
    });
    return {
      permission: 'granted',
      location: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      },
    };
  } catch (error) {
    // PERMISSION_DENIED = 1. Everything else (position unavailable, timeout) is treated the same
    // way from her side: she does not know, so she asks.
    return {
      permission: error && error.code === 1 ? 'denied' : 'unsupported',
      location: null,
    };
  }
}

/**
 * The permission state, without prompting.
 *
 * WebKit reports 'granted' truthfully once permission has been granted, and deliberately masks
 * 'denied' as 'prompt' to resist fingerprinting. So: 'granted' means we may acquire silently;
 * anything else means "unknown — do not pre-prompt", and we wait for a turn that needs it.
 */
export async function locationPermissionState() {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown';
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state || 'unknown';
  } catch (e) {
    return 'unknown';
  }
}
